import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { attentionHeadline } from "@/lib/kpi-labels";
import { tonePillClass } from "@/lib/tone";
import { dataReference, partialMonths, selectGrainSeries } from "@/lib/time-range";
import { DataIssueBanner, UpdatedStamp } from "@/components/data";

import { getFreshness, getOverview, messageFor, pluginFrom } from "./api";
import { ScopeNote } from "./components/ScopeNote";
import { TopProducts } from "./components/TopProducts";
import { UnitsHeadline } from "./components/UnitsHeadline";
import { ModuleControls, useControls } from "./controls";
import {
  chartRangeFor,
  DATA_FRESH_MAX_DAYS,
  freshnessState,
  productsFor,
  regionFromDeepLink,
  regionLabel,
  regionsFrom,
  selectWindow,
  seriesBundle,
  severityTone,
  unitsSeriesBundle,
} from "./format";
import { openMonthMessage, staleMonthMessage } from "./TrafficPage";

/**
 * The all-regions view.
 *
 * The per-region freshness strip is not decoration. This tile AGGREGATES every
 * region, so its own latest date is the FRESHEST region's - a market that
 * stopped reporting a fortnight ago is invisible in the headline and visible
 * only here. Meteoric learned that the hard way; the strip carries across
 * unchanged in intent.
 */
export function OverviewTab({ areaName }: { areaName: string }) {
  const controls = useControls();

  const overview = useQuery({
    queryKey: ["meteoric_units_sold", "overview"],
    queryFn: getOverview,
  });

  const freshness = useQuery({
    queryKey: ["meteoric_units_sold", "freshness"],
    queryFn: getFreshness,
  });

  if (overview.isPending) return <div className="flex flex-col gap-6"><ModuleControls controls={controls} /><Skeleton className="h-96 w-full" /></div>;

  if (overview.isError) {
    return <div className="flex flex-col gap-6"><ModuleControls controls={controls} />
      <Alert>
        <AlertTitle>Units Sold unavailable</AlertTitle>
        <AlertDescription>{messageFor(overview.error, areaName)}</AlertDescription>
      </Alert>
    </div>;
  }

  const plugin = pluginFrom(overview.data.data);

  // A read that SUCCEEDED but carries no Units Sold plugin is a true, specific
  // state - the daily aggregation ran with the plugin switched off, or has not
  // run since it was switched on. It is deliberately not folded into the error
  // branch above: nobody should be paged for a deliberate configuration.
  if (!plugin) {
    return <div className="flex flex-col gap-6"><ModuleControls controls={controls} />
      <Alert>
        <AlertTitle>Units Sold is not in the latest aggregation</AlertTitle>
        <AlertDescription>
          The daily run completed
          {overview.data.data.generated_at ? ` at ${overview.data.data.generated_at}` : ""} but produced no
          Units Sold figures. The plugin is switched off, or it has not run since it was switched on.
        </AlertDescription>
      </Alert>
    </div>;
  }

  const { tile, products, attention_items: attention } = plugin;
  const window = selectWindow(tile, controls.range);
  // Trim against the DATA's freshness date, as RegionTab does (bug 3e90402f).
  const reference = dataReference(overview.data.meta.data_as_of, overview.data.data.generated_at);
  const chartRange = chartRangeFor(controls.grain, controls.range);
  const growthPoints = selectGrainSeries(seriesBundle(tile), controls.grain, chartRange, reference);
  const unitsPoints = selectGrainSeries(unitsSeriesBundle(tile), controls.grain, chartRange, reference);
  const monthIssues = controls.grain === "monthly"
    ? partialMonths(seriesBundle(tile).monthly, reference).map((issue) => ({
      ...issue,
      message: issue.kind === "partial"
        ? openMonthMessage(
          issue.month,
          !growthPoints.some((point) => point.m === issue.month)
            && !unitsPoints.some((point) => point.m === issue.month)
            ? growthPoints.at(-1)?.m ?? unitsPoints.at(-1)?.m ?? null
            : null,
          { dataThrough: overview.data.meta.data_as_of, generatedAt: overview.data.data.generated_at },
        )
        : staleMonthMessage(issue.month),
    }))
    : [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ModuleControls controls={controls} />
        <UpdatedStamp generatedAt={overview.data.data.generated_at} />
      </div>

      {/* A failed freshness read is stated, never silently dropped: "we could
 not check the regions" and "every region is current" look identical
 when the strip simply does not render. */}
      {freshness.isError ? (
        <Alert>
          <AlertTitle>Region freshness could not be checked</AlertTitle>
          <AlertDescription>
            The headline below still shows, but it aggregates every region - a market that stopped reporting
            would not be visible in it.
          </AlertDescription>
        </Alert>
      ) : (
        freshness.data && <FreshnessStrip freshness={freshness.data.data} />
      )}

      {/* Raw (untrimmed) monthly bundle, not the windowed `growthPoints` -
          the still-open current month a chart's own trim removes is exactly
          the bucket `partialMonths` looks for. */}
      {monthIssues.some((issue) => issue.kind === "partial") && (
        <ScopeNote>
          {monthIssues.filter((issue) => issue.kind === "partial").map((issue) => issue.message).join("; ")}
        </ScopeNote>
      )}
      <DataIssueBanner issues={monthIssues.filter((issue) => issue.kind === "stale")} />

      <UnitsHeadline
        tile={tile}
        window={window}
        range={controls.range}
        grain={controls.grain}
        growthPoints={growthPoints}
        unitsPoints={unitsPoints}
      />

      <TopProducts
        products={productsFor(window, products)}
        total={window.total}
        totalAll={window.total_all}
        periodLabel={window.period ?? "Period pending"}
        showTopRegion
      />

      {attention.length > 0 && (
        <Card>
          <CardContent className="flex flex-col gap-3">
            <h2 className="text-base font-semibold text-ks-ink">Attention items</h2>
            <ul className="flex flex-col divide-y divide-border">
              {attention.map((item) => {
                const region = regionFromDeepLink(item.deep_link);
                const content = (
                  <div className="flex min-h-11 items-center gap-3 py-2 text-sm">
                    <span
                      aria-hidden
                      className={`h-2 w-2 rounded-full ${
                        item.severity === "critical"
                          ? "bg-ks-fail"
                          : item.severity === "warn"
                            ? "bg-ks-warn"
                            : "bg-ks-grey-2"
                      }`}
                    />
                    <Badge
                      variant="outline"
                      className={`capitalize ${tonePillClass(severityTone(item.severity))}`}
                    >
                      {item.severity}
                    </Badge>
                    <span className="text-ks-ink">
                      {attentionHeadline(item.headline, tile.kpi_id, tile.kpi_name)}
                    </span>
                    {region && <ChevronRight aria-hidden className="ml-auto size-4 text-muted-foreground" />}
                  </div>
                );
                return (
                  <li key={`${item.severity}-${item.headline}`} className="hover:bg-muted">
                    {region ? (
                      <Link
                        to="/app/$area/$module/$tab"
                        params={{ area: "customer_acquisition", module: "meteoric_units_sold", tab: "units" }}
                        search={{ f_region: region }}
                        data-nonmutating
                      >
                        {content}
                      </Link>
                    ) : (
                      content
                    )}
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function FreshnessStrip({ freshness }: { freshness: Record<string, string | null> }) {
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Region data through
        </span>
        {regionsFrom(freshness).map(({ region, date }) => {
          const state = freshnessState(date);
          return (
            <span key={region} className="flex items-center gap-2 text-sm">
              <span className="font-medium text-ks-ink">{regionLabel(region)}</span>
              <span className="tabular-nums text-muted-foreground">{date ?? " - "}</span>
              <Badge variant="outline" className={tonePillClass(state.tone)}>
                {state.word}
                {state.ageLabel ? ` · ${state.ageLabel}` : ""}
              </Badge>
            </span>
          );
        })}
        {/* The threshold is written down where the badges are. A reader who
 cannot see what "Lagging" means cannot act on it. */}
        <span className="text-xs text-muted-foreground">
          Live is within {DATA_FRESH_MAX_DAYS} days of today.
        </span>
      </CardContent>
    </Card>
  );
}
